import { z } from "zod";
import { workflowDryRunReceiptSchema } from "./workflow-dry-run.js";
import { durableWorkflowDefinitionSchema } from "./workflow-resources.js";
import { workflowNodeSchema } from "./workflow-nodes.js";
export { workflowNodeSchema } from "./workflow-nodes.js";
const identifier = z.string().regex(/^[a-zA-Z_][a-zA-Z0-9_]{0,63}$/);
/**
 * The largest request that carries a whole workflow definition (create, validate, a new version): its Python may run to
 * 100,000 characters, beside its nodes, sources and extraction schemas, so the public gateway's general limit is too
 * small for a definition the contract accepts.
 */
export const WORKFLOW_DEFINITION_MAX_BODY_BYTES = 512 * 1024;
/** Explicit data dependencies; arbitrary code is retained for review, never eval'd by a client. */
export const workflowDefinitionSchema = z
    .object({
    schemaVersion: z.literal(1),
    title: z.string().trim().min(1).max(160),
    description: z.string().max(4000),
    python: z.string().min(1).max(100_000),
    nodes: z.array(workflowNodeSchema).min(1).max(64),
})
    .strict()
    .superRefine((definition, ctx) => {
    const seen = new Set();
    for (const [index, node] of definition.nodes.entries()) {
        if (seen.has(node.id))
            ctx.addIssue({
                code: "custom",
                path: ["nodes", index, "id"],
                message: "Node IDs must be unique",
            });
        if (new Set(node.inputs).size !== node.inputs.length)
            ctx.addIssue({
                code: "custom",
                path: ["nodes", index, "inputs"],
                message: "Dependencies must be unique",
            });
        for (const dependency of node.inputs) {
            if (!seen.has(dependency))
                ctx.addIssue({
                    code: "custom",
                    path: ["nodes", index, "inputs"],
                    message: `Dependency ${dependency} must name an earlier node (cycles are forbidden)`,
                });
        }
        seen.add(node.id);
    }
});
export const workflowAnyDefinitionSchema = z.union([
    workflowDefinitionSchema, durableWorkflowDefinitionSchema,
]);
const workflowJudgmentInstructionsSchema = z.string().min(1).max(8000);
export const workflowJudgmentQuestionSchema = z.discriminatedUnion("type", [
    z
        .object({
        type: z.literal("choice"),
        instructions: workflowJudgmentInstructionsSchema,
        criteria: z
            .record(z.string().min(1), z.string().min(1))
            .refine((value) => Object.keys(value).length >= 2 && Object.keys(value).length <= 100),
    })
        .strict(),
    z
        .object({
        type: z.literal("noul"),
        instructions: workflowJudgmentInstructionsSchema,
    })
        .strict(),
    z
        .object({
        type: z.literal("score"),
        instructions: workflowJudgmentInstructionsSchema,
        criteria: z.array(z.string().min(1)).min(2).max(20),
    })
        .strict(),
]);
export const workflowJudgmentRequestSchema = z
    .object({
    state: z.json(),
    questions: z
        .record(z.string().min(1), workflowJudgmentQuestionSchema)
        .refine((value) => Object.keys(value).length > 0 && Object.keys(value).length <= 100),
})
    .strict();
/** The question Twin Judge's second reader asks of every finding on a workflow review. */
export const workflowSecondReaderQuestion = "Does the proposed value follow from this evidence?";
const workflowJudgmentProbabilitySchema = z.number().min(0).max(1);
/** One Twin Judge answer, typed as its question was asked. */
export const workflowJudgmentAnswerSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("choice"), choice: z.string(),
        probabilities: z.record(z.string(), workflowJudgmentProbabilitySchema),
        confidence: workflowJudgmentProbabilitySchema }),
    z.object({ type: z.literal("noul"), noul: workflowJudgmentProbabilitySchema }),
    z.object({ type: z.literal("score"), score: z.number(), confidence: workflowJudgmentProbabilitySchema }).passthrough(),
]);
export const workflowJudgmentResponseSchema = z.object({
    model: z.string(), answers: z.record(z.string(), workflowJudgmentAnswerSchema),
}).passthrough();
/** A judge step's retained result: what was asked, and what Twin Judge answered. */
export const workflowJudgmentEvidenceSchema = z.object({
    request: workflowJudgmentRequestSchema, response: workflowJudgmentResponseSchema,
}).strict();
/** A second reading that could not be had, retained so the review says so and a replay does not retry it. */
export const workflowSecondReaderFailureSchema = z.object({
    failed: z.object({ reason: z.string().min(1).max(300) }).strict(),
}).strict();
export const workflowPreviewSchema = z
    .object({
    dryRun: workflowDryRunReceiptSchema.optional(),
    version: z.number().int().positive(),
    status: z.enum(["succeeded", "failed"]),
    nodes: z
        .array(z.object({ id: identifier, output: z.json() }).strict())
        .max(64),
    error: z.string().max(4000).nullable(),
})
    .strict()
    .refine((value) => JSON.stringify(value).length <= 1_000_000, "Preview exceeds 1 MB");
export const workflowTitleSchema = z.string().trim().min(1).max(160);
export const workflowSchema = z.object({
    access: z.object({
        ownerId: z.string(),
        ownerName: z.string().nullable(),
        /** Owners and organisation administrators can edit definitions and metadata. */
        canEdit: z.boolean(),
        /** Execution and scheduling continue to require the owner's authority. */
        canManage: z.boolean(),
    }),
    id: z.string(),
    sessionId: z.string(),
    version: z.number().int().positive(),
    definition: workflowAnyDefinitionSchema,
    /** User-set display name; null shows the definition's title. */
    title: workflowTitleSchema.nullable(),
    archivedAt: z.number().nullable(),
    digest: z.string(),
    updatedAt: z.number(),
    preview: workflowPreviewSchema.nullable(),
    previewRun: z.object({ id: z.string(), createdAt: z.number() }).nullable(),
});
export const workflowDisplayTitle = (workflow) => workflow.title ?? workflow.definition.title;
export const workflowStatusFilterSchema = z.enum(["active", "archived", "all"]);
/** Rename or archive without touching the versioned definition. */
export const updateWorkflowRequestSchema = z
    .object({
    sessionId: z.string().min(1),
    title: workflowTitleSchema.nullable().optional(),
    archived: z.boolean().optional(),
})
    .strict()
    .refine((value) => value.title !== undefined || value.archived !== undefined, "Provide a title or archived state");
export const listWorkflowsResponseSchema = z.object({
    workflows: z.array(workflowSchema),
});
export const saveWorkflowSchema = z
    .object({
    expectedVersion: z.number().int().nonnegative(),
    definition: workflowDefinitionSchema,
})
    .strict();
/** The authoring agent's save of either a draft or a durable definition; activation stays the owner's review. */
export const saveAnyWorkflowSchema = z
    .object({
    expectedVersion: z.number().int().nonnegative(),
    definition: workflowAnyDefinitionSchema,
})
    .strict();
export const workflowVersionSchema = z.object({
    version: z.number().int().positive(),
    definition: workflowAnyDefinitionSchema,
    createdAt: z.number(),
    restoredFromVersion: z.number().int().positive().nullable(),
});
export const listWorkflowVersionsRequestSchema = z
    .object({
    sessionId: z.string().min(1),
    beforeVersion: z.coerce.number().int().positive().optional(),
})
    .strict();
export const listWorkflowVersionsResponseSchema = z.object({
    versions: z.array(workflowVersionSchema),
    nextBeforeVersion: z.number().int().positive().nullable(),
});
export const restoreWorkflowRequestSchema = z
    .object({
    sessionId: z.string().min(1),
    version: z.number().int().positive(),
    expectedVersion: z.number().int().positive(),
})
    .strict();
